import {
  formatRecipientLabel,
  normalizeRecipientOption,
  uniqueRecipientsByKey,
} from '@/composables/people/recipientOptions';
import collaboratorsService from '@/services/collaborators';
import expenseReceiptsService from '@/services/expenseReceipts';
import suppliersService from '@/services/suppliers';
import { SEND_EMAIL_TEXT } from '@/texts/catalog/sendEmail';
import { extractCollection, extractPayload } from '@/utils/dataNormalization';
import { formatCurrency } from '@/utils/numberFormat';
import { resolveText } from '@/utils/textValue';

const uniqueById = (items = []) => {
  const seen = new Set();
  const uniqueItems = [];

  (Array.isArray(items) ? items : []).forEach((item) => {
    const id = resolveText(item?.id || item?.document_id || '', '');
    if (id === '' || seen.has(id)) {
      return;
    }

    seen.add(id);
    uniqueItems.push(item);
  });

  return uniqueItems;
};

const resolveExpenseReceiptText = () => {
  const section = SEND_EMAIL_TEXT?.expenseReceipts;
  return section && typeof section === 'object' ? section : {};
};

const normalizeRecipientsCollection = (collection = [], prefix = 'recipient') => {
  const normalized = (Array.isArray(collection) ? collection : [])
    .map((item, index) => normalizeRecipientOption(item, index, prefix))
    .filter(Boolean);

  return uniqueRecipientsByKey(normalized);
};

const resolveExpenseReceiptContextData = (vm) => {
  const contextData = vm.contextData && typeof vm.contextData === 'object' ? vm.contextData : {};
  const expenseReceiptData = contextData.expenseReceiptData && typeof contextData.expenseReceiptData === 'object'
    ? contextData.expenseReceiptData
    : {};

  const expenseReceiptId = Number(contextData.id || contextData.expenseReceiptId || expenseReceiptData.id || 0) || 0;
  const expenseReference = resolveText(
    expenseReceiptData.folio || expenseReceiptData.code || contextData.folio,
    expenseReceiptId > 0 ? `#${expenseReceiptId}` : ''
  );

  const supplierName = resolveText(
    contextData.supplierName
      || expenseReceiptData.supplier_name
      || expenseReceiptData.provider_name
      || expenseReceiptData.receiver_business_name,
    ''
  );

  const total = Number(contextData.total ?? expenseReceiptData.total ?? expenseReceiptData.amount ?? 0) || 0;
  const currency = resolveText(contextData.currency || expenseReceiptData.currency, 'MXN');
  const supplierId = Number(
    contextData.supplierId
      || contextData.providerId
      || expenseReceiptData.supplier_id
      || expenseReceiptData.provider_id
      || expenseReceiptData.company_id
      || 0
  ) || 0;

  return {
    expenseReceiptId,
    expenseReceiptData,
    expenseReference,
    supplierName,
    supplierId,
    total,
    currency,
  };
};

const resolveExpenseReceiptOwnerId = (expenseContext = {}) => {
  const expenseReceiptData = expenseContext.expenseReceiptData && typeof expenseContext.expenseReceiptData === 'object'
    ? expenseContext.expenseReceiptData
    : {};

  return Number(
    expenseContext.supplierId
      || expenseContext.providerId
      || expenseReceiptData.supplier_id
      || expenseReceiptData.provider_id
      || expenseReceiptData.company_id
      || 0
  ) || 0;
};

const mapSupplierRecipientsForMailing = (rows = []) => {
  return (Array.isArray(rows) ? rows : [])
    .filter((item) => {
      const email = resolveText(item?.email, '');
      return email !== '';
    })
    .map((item) => {
      return {
        id: resolveText(item?.id, ''),
        name: resolveText(item?.name, ''),
        email: resolveText(item?.email, ''),
        invoice_mailing: Number(item?.invoice_mailing || 0) || 0,
        payments_mailing: Number(item?.payments_mailing || 0) || 0,
      };
    });
};

const resolveExpenseReceiptRecipients = async (ownerId = 0) => {
  if (ownerId <= 0) {
    return [];
  }

  const response = await suppliersService.personsIndex(ownerId);
  const source = extractCollection(response);

  return mapSupplierRecipientsForMailing(source);
};

const resolveExpenseReceiptPrimaryRecipient = (expenseContext = {}) => {
  const expenseReceiptData = expenseContext.expenseReceiptData && typeof expenseContext.expenseReceiptData === 'object'
    ? expenseContext.expenseReceiptData
    : {};

  const name = resolveText(
    expenseReceiptData.supplier_name
      || expenseReceiptData.provider_name
      || expenseReceiptData.receiver_business_name
      || expenseContext.supplierName,
    ''
  );

  const email = resolveText(
    expenseReceiptData.supplier_email
      || expenseReceiptData.provider_email
      || expenseReceiptData.email,
    ''
  );

  return {
    id: resolveText(
      expenseContext.supplierId
      || expenseReceiptData.supplier_id
      || expenseReceiptData.provider_id
      || expenseReceiptData.company_id
      || '',
      ''
    ),
    name,
    email,
    label: formatRecipientLabel(name, email),
  };
};

const resolveExpenseReceiptFallbackRecipients = (expenseContext = {}, primaryRecipient = {}) => {
  const candidates = [];

  if (primaryRecipient && typeof primaryRecipient === 'object') {
    candidates.push(primaryRecipient);
  }

  const expenseReceiptData = expenseContext.expenseReceiptData && typeof expenseContext.expenseReceiptData === 'object'
    ? expenseContext.expenseReceiptData
    : {};

  [
    'persons',
    'people',
    'contacts',
    'contact_people',
    'copyRecipients',
    'ccRecipients',
    'sendCcOptions',
  ].forEach((key) => {
    const list = Array.isArray(expenseReceiptData[key]) ? expenseReceiptData[key] : [];
    list.forEach((item) => {
      candidates.push(item);
    });
  });

  return normalizeRecipientsCollection(candidates, 'fallback-recipient');
};

const buildExpenseReceiptSummaryMeta = (expenseContext = {}) => {
  const summaryText = resolveExpenseReceiptText()?.summary || {};
  const sendTypeLabelValue = resolveText(summaryText?.sendTypeValue, 'Pago de egreso');
  const reference = resolveText(expenseContext.expenseReference, '');
  const sendTypeValue = `${sendTypeLabelValue}${reference !== '' ? ` ${reference}` : ''}`.trim();

  return {
    sendTypeLabel: resolveText(summaryText?.sendTypeLabel, ''),
    sendTypeValue,
    counterpartLabel: resolveText(summaryText?.supplierLabel || summaryText?.counterpartLabel, ''),
    counterpartValue: resolveText(expenseContext.supplierName, ''),
    totalLabel: resolveText(summaryText?.totalLabel, ''),
    totalValue: formatCurrency(expenseContext.total, { currency: expenseContext.currency || 'MXN' }),
  };
};

const resolveInitialToRecipientIds = (vm) => {
  const options = Array.isArray(vm.normalizedToRecipientOptions) ? vm.normalizedToRecipientOptions : [];

  const preferredIds = options
    .filter((item) => {
      const invoiceMailing = Number(item?.invoice_mailing || 0) === 1;
      const paymentsMailing = Number(item?.payments_mailing || 0) === 1;
      return (invoiceMailing || paymentsMailing) && resolveText(item?.email, '') !== '';
    })
    .map((item) => item.id);

  if (preferredIds.length > 0) {
    return preferredIds;
  }

  const primaryId = resolveText(vm.normalizedPrimaryRecipient?.id, '');
  if (primaryId !== '') {
    return [primaryId];
  }

  const firstRecipient = options.find((item) => resolveText(item?.email, '') !== '');
  return firstRecipient ? [firstRecipient.id] : [];
};

const resolveInitialCcRecipientIds = (vm) => {
  const options = Array.isArray(vm.normalizedCcRecipientOptions) ? vm.normalizedCcRecipientOptions : [];

  return options
    .filter((item) => {
      const invoiceMailing = Number(item?.invoice_mailing || 0) === 1;
      const paymentsMailing = Number(item?.payments_mailing || 0) === 1;
      return (invoiceMailing || paymentsMailing) && resolveText(item?.email, '') !== '';
    })
    .map((item) => item.id);
};

const resolveDefaultMessage = (supplierName = '') => {
  const defaultMessage = resolveExpenseReceiptText()?.defaultMessage;
  if (typeof defaultMessage === 'function') {
    return String(defaultMessage(supplierName || '') || '');
  }

  return resolveText(defaultMessage, '');
};

const hydrateExpenseReceiptContextData = async (vm) => {
  const expenseContext = resolveExpenseReceiptContextData(vm);
  const ownerId = resolveExpenseReceiptOwnerId(expenseContext);
  const primaryRecipient = resolveExpenseReceiptPrimaryRecipient(expenseContext);

  vm.text = resolveExpenseReceiptText();
  vm.summaryMeta = buildExpenseReceiptSummaryMeta(expenseContext);
  vm.primaryRecipient = primaryRecipient;
  vm.messageType = 'expense_receipt';
  vm.messageTypeCandidates = ['expense_receipt'];

  const supplierName = resolveText(expenseContext.supplierName, '').replace(/\s+/g, ' ').trim();
  vm.messageVariables = {
    supplier: supplierName,
    supplierName,
    NOMBRE_PROVEEDOR: supplierName,
  };
  vm.messageLoadingText = resolveText(vm.text?.messageLoadingText, '');
  vm.initialMessage = resolveDefaultMessage(supplierName);
  vm.recipientsLoading = ownerId > 0;

  const [recipientsResult, copyResult, documentsResult] = await Promise.allSettled([
    ownerId > 0
      ? resolveExpenseReceiptRecipients(ownerId)
      : Promise.resolve([]),
    collaboratorsService.invoiceMailingSelectors(),
    expenseContext.expenseReceiptId > 0
      ? expenseReceiptsService.documentsIndex(expenseContext.expenseReceiptId)
      : Promise.resolve({}),
  ]);

  let recipientOptions = [];
  if (recipientsResult.status === 'fulfilled') {
    recipientOptions = normalizeRecipientsCollection(recipientsResult.value, 'person');
  }

  if (recipientOptions.length === 0) {
    recipientOptions = resolveExpenseReceiptFallbackRecipients(expenseContext, primaryRecipient);
  }

  let copyOptions = [];
  if (copyResult.status === 'fulfilled') {
    copyOptions = normalizeRecipientsCollection(extractCollection(copyResult.value), 'collaborator');
  }

  let serviceDocuments = [];
  if (documentsResult.status === 'fulfilled') {
    const payload = extractPayload(documentsResult.value);
    serviceDocuments = Array.isArray(payload?.documents) ? payload.documents : [];
  }

  vm.recipientOptions = recipientOptions;
  vm.copyOptions = copyOptions;
  vm.invoiceAttachmentOptions = [];
  vm.documentAttachmentOptions = uniqueById(serviceDocuments);
  vm.recipientsLoading = false;
  vm.initialToRecipientIds = resolveInitialToRecipientIds(vm);
  vm.initialCcRecipientIds = resolveInitialCcRecipientIds(vm);
};

export const sendEmailExpenseReceiptContextConfig = Object.freeze({
  supportsInvoiceAttachments: true,
  allowInvoiceAttachmentSelection: false,
  resolveText() {
    return resolveExpenseReceiptText();
  },
  hydrateContextData: hydrateExpenseReceiptContextData,
  resolveSelectedInvoiceAttachmentItems() {
    return [];
  },
  resolveDefaultInvoiceAttachmentItem(vm) {
    const summaryValue = resolveText(vm.summaryData?.sendTypeValue, 'Pago de egreso');
    const labelSuffix = '(PDF)';

    return {
      id: 'expense-receipt-default-files',
      label: `${summaryValue} ${labelSuffix}`.trim(),
      group: 'invoice-default',
      locked: true,
    };
  },
  resolveInvoiceAttachmentSummaryLabel(vm, option) {
    return String(option?.label || '').trim();
  },
});
